import React, { useEffect, useState } from 'react';
import _ from 'underscore';
import { useTranslation } from 'react-i18next';

import Badge from 'components/v2/Badge';
import Container from 'components/v2/container';
import SessionStore from 'stores/SessionStore';
import CustomerSearch from 'components/site/customer-search';
import Multiselect from 'components/v2/multi-select';
import TextField from 'components/v2/forms/TextField';
import { sluggify } from 'utils/localization/utils';
import LaneRatesTable from './LaneRatesTable';
import { useLocation } from 'react-router-dom/cjs/react-router-dom';

const STATUS = [
  { name: 'Confirmed', slug: 'confirmed', localizationSlug: 'CONFIRMED' },
  { name: 'Awaiting', slug: 'awaiting', localizationSlug: 'AWAITING' },
  { name: 'Expired', slug: 'expired', localizationSlug: 'EXPIRED' },
  { name: 'Declined', slug: 'declined', localizationSlug: 'DECLINED' },
];
const USER_TYPES = [
  {
    name: 'Carrier',
    slug: 'carrier',
    localizationSlug: 'CARRIER',
    titleize: true,
  },
  {
    name: 'Shipper',
    slug: 'shipper',
    localizationSlug: 'SHIPPER',
    titleize: true,
  },
];
const ORIGIN = 'Origin';
const DESTINATION = 'Destination';

export default function LaneRates() {
  const [user, setUser] = useState(SessionStore.getUser());
  const [customerType, setUserTypeMulti] = useState([]);
  const [origin, setOrigin] = useState('');
  const [loadingItems, setLoadingItems] = useState(false);
  const [originZone, setOriginZone] = useState('');
  const [originCity, setOriginCity] = useState('');
  const [originState, setOriginState] = useState('');
  const [destination, setDestination] = useState('');
  const [destinationZone, setDestinationZone] = useState('');
  const [destinationCity, setDestinationCity] = useState('');
  const [destinationState, setDestinationState] = useState('');
  const [status, setStatusMulti] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [clearUserType, setClearUserType] = useState(false);
  const [clearStatusMulti, setClearStatusMulti] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    // get previous filters if any
    const listQueryFilterParameters = location.state?.listQueryFilterParameters;
    if (listQueryFilterParameters) {
      const {
        originZone,
        destinationZone,
        customers,
        customerTypes,
        statuses,
      } = listQueryFilterParameters;
      setOriginZone(originZone);
      setDestinationZone(destinationZone);
      setCustomers(customers);
      setUserTypeMulti(customerTypes);
      setStatusMulti(statuses);
    }
  }, []);

  const filterByCustomer = (value) => {
    setCustomers((currentCustomers) => {
      return _.uniq(currentCustomers.concat(value));
    });
  };

  const renderCustomerFilter = () => {
    return (
      <div className="mb-4">
        <CustomerSearch
          disabled={loadingItems}
          clearOnSelect
          type="customer"
          onSelect={(_item, value) => {
            filterByCustomer(value);
          }}
          placeholder={t('SEARCH_FOR_CUSTOMER')}
        />
      </div>
    );
  };

  const handleRemoveCustomer = (id) => {
    setCustomers((currentCustomers) => {
      return _.uniq(
        currentCustomers.filter((customer) => customer.value !== id),
      );
    });
  };

  const handleSelectUserTypeMulti = (value) => {
    let selectedValue = value.map((val) => {
      return val.name;
    });
    setUserTypeMulti(selectedValue);
  };

  const renderUserTypesMulti = () => {
    const { t } = useTranslation();
    return (
      <div className="mb-4">
        <Multiselect
          disabled={loadingItems}
          label={t('USER_TYPE')}
          items={USER_TYPES}
          onSelect={handleSelectUserTypeMulti}
          clear={clearUserType}
          dataTestId='user-types-multiselect'
        />
      </div>
    );
  };

  const handleSelectStatusMulti = (value) => {
    let selectedValue = value.map((val) => {
      return val.name;
    });
    setStatusMulti(selectedValue);
  };

  const renderFilterbyStatusMulti = () => {
    const { t } = useTranslation();
    return (
      <div className="mb-4">
        <Multiselect
          disabled={loadingItems}
          label={t('STATUS')}
          items={STATUS}
          onSelect={handleSelectStatusMulti}
          clear={clearStatusMulti}
          testDataId='status-multiselect'
        />
      </div>
    );
  };

  const getHandleValueChange = (type) => {
    return (value) => {
      if (type === ORIGIN) {
        setOriginZone(value);
      } else {
        setDestinationZone(value);
      }
    };
  };

  const renderZoneField = (type) => {
    const { t } = useTranslation();
    return (
      <TextField
        type="text"
        disabled={loadingItems}
        value={type === ORIGIN ? originZone : destinationZone}
        placeholder={type === ORIGIN ? t('ORIGIN_ZONE') : t('DESTINATION_ZONE')}
        onChange={getHandleValueChange(type)}
        name={type === ORIGIN ? 'origin' : 'destination'}
      />
    );
  };

  function renderBadgeList(list) {
    return list.map((l, index) => {
      let name = index === list.length - 1 ? l : `${l},`;
      let localizedName = t(sluggify(name));
      return <span className="ml-2">{localizedName}</span>;
    });
  }
  const clearCustomerMultiselect = () => {
    setUserTypeMulti([]);
    setClearUserType(!clearUserType);
  };

  const clearStatusMultiselect = () => {
    setStatusMulti([]);
    setClearStatusMulti(!clearStatusMulti);
  };

  const renderActiveFilters = () => {
    return (
      <div className="flex mb-4">
        {customers.length || originZone || destinationZone
          ? `${t('ACTIVE_FILTERS')}: `
          : t('ACTIVE_FILTER_NONE')}
        {customers.map((customer, i) => (
          <Badge
            color="blue"
            className="mr-1"
            withRemove
            onRemove={() => setCustomers(customers.filter((c, ci) => ci !== i))}
            key={customer.name}
          >
            {t('CUSTOMER_WITH_NAME', {
              customerName: customer.name,
            })}
          </Badge>
        ))}
        {originZone ? (
          <Badge
            color="blue"
            className="mr-1"
            withRemove
            onRemove={() => setOriginZone('')}
          >
            {t('ORIGIN_ZONE_WITH_VALUE', {
              originZone,
            })}
          </Badge>
        ) : null}
        {destinationZone ? (
          <Badge
            color="blue"
            className="mr-1"
            withRemove
            onRemove={() => setDestinationZone('')}
          >
            {t('DESTINATION_ZONE_WITH_VALUE', {
              destinationZone,
            })}
          </Badge>
        ) : null}
        {customerType && customerType.length ? (
          <Badge
            color="blue"
            className="mr-1"
            withRemove
            onRemove={() => clearCustomerMultiselect()}
          >
            {`${t('TYPE')}: `}
            {renderBadgeList(customerType)}
          </Badge>
        ) : null}
        {status && status.length ? (
          <Badge
            color="blue"
            className="mr-1"
            withRemove
            onRemove={() => clearStatusMultiselect()}
          >
            {`${t('TYPE')}: `}
            {renderBadgeList(status)}
          </Badge>
        ) : null}
      </div>
    );
  };

  return (
    <Container className="py-10">
      <h3 className="my-0 mb-4">{t('LANE_RATES')}</h3>
      <div>{renderActiveFilters()}</div>
      <div className="flex flex-wrap" data-testid="lane-rates-container">
        <div className="w-full md:w-1/6 mr-3">{renderUserTypesMulti()}</div>
        <div className="w-full md:w-1/6 px-3">{renderCustomerFilter()}</div>
        <div className="w-full md:w-1/6 px-3">{renderZoneField(ORIGIN)}</div>
        <div className="w-full md:w-1/6 px-3">
          {renderZoneField(DESTINATION)}
        </div>
        <div className="w-full md:w-1/6 px-3">
          {renderFilterbyStatusMulti()}
        </div>
      </div>
      <LaneRatesTable
        setLoadingItems={setLoadingItems}
        customer={customers?.length ? _.map(customers, '_id') : null}
        customers={customers}
        customerType={
          customerType?.length
            ? _.map(customerType, (type) => type.toLowerCase())
            : null
        }
        customerTypes={customerType}
        originZone={originZone?.length ? originZone : null}
        destinationZone={destinationZone?.length ? destinationZone : null}
        status={
          status?.length
            ? _.map(status, (status) => status.toLowerCase())
            : null
        }
        statuses={status}
      />
    </Container>
  );
}
